import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import useUser from "@/users/useUser";
import useUserClassroomGet from "@/users/useUserClassroomGet";
import { useFetchAttributes } from "@/users/useUserAttributes";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import { teacherQueries } from "@/teacher";
import { schoolAdminQueries } from "@/schoolAdmin";

import mapUserToPendoProperties from "./mapUserToPendoProperties";
import type { UserClassroom } from "@/classrooms/queries/queries.types";

import type { PendoUserProperties } from "@newsela/snitch";

export const GATE_FAIL_USER: PendoUserProperties = {
  id: 0,
  unique_id: "",
  is_authenticated: false,
  student: undefined,
  grade_levels: undefined,
  classrooms: undefined,
};

const CLASSROOM_PAGE_SIZE = 100;
const CLASSROOM_PARAMS = { hide_archived: false };

type ClassroomsResponse = { results?: UserClassroom[] } | undefined;

export const useAggregatedPendoUser = (): {
  user: PendoUserProperties;
  isReady: boolean;
} => {
  const { data: userV2, loading: isLoadingUser } = useUser();
  const { userAttributes, loadingUserAttributes } = useFetchAttributes();
  const { isImpersonated, isLoadingFeatures } = useGetUserProperties();
  const { data: classroomsData, loading: isLoadingClassrooms } =
    useUserClassroomGet(userV2?.pk, CLASSROOM_PARAMS, CLASSROOM_PAGE_SIZE);
  const userClassrooms = (classroomsData as ClassroomsResponse)?.results;

  const isStudent = userV2?.roles?.includes("student") ?? false;
  const isTeacher = Boolean(userV2?.teacher) && !isStudent;
  const isSchoolAdmin = Boolean(userV2?.school_admin) && !isStudent;

  const { data: teacherSchools, isLoading: isLoadingTeacherSchools } = useQuery(
    {
      ...teacherQueries.schools(),
      enabled: isTeacher,
    },
  );
  const { data: schoolAdminSchools, isLoading: isLoadingSchoolAdminSchools } =
    useQuery({
      ...schoolAdminQueries.schools(),
      enabled: isSchoolAdmin,
    });

  const isReady =
    !isLoadingUser &&
    !loadingUserAttributes &&
    !isLoadingFeatures &&
    !isLoadingClassrooms &&
    !(isTeacher && isLoadingTeacherSchools) &&
    !(isSchoolAdmin && isLoadingSchoolAdminSchools);

  const user = useMemo<PendoUserProperties>(() => {
    if (!isReady) return GATE_FAIL_USER;
    return (
      mapUserToPendoProperties({
        userV2,
        userAttributes,
        isImpersonated,
        userClassrooms,
        teacherSchools,
        schoolAdminSchools,
      }) ?? GATE_FAIL_USER
    );
  }, [
    isReady,
    userV2,
    userAttributes,
    isImpersonated,
    userClassrooms,
    teacherSchools,
    schoolAdminSchools,
  ]);

  return { user, isReady };
};

export default useAggregatedPendoUser;
